import { useEffect, useState } from 'react';

import { useLoginUserMutation } from '@crehana/auth';
import { useTranslation } from '@crehana/i18n';
// biome-ignore lint/style/noRestrictedImports: legacy code - style/noRestrictedImports
import { PrimaryButton, TextField } from '@crehana/ui';
import {
  allowFloatingShoppingCartReminderVisibility,
  setRecentlyLoggedStatus,
} from '@crehana/web/auth/utils';
// biome-ignore lint/style/noRestrictedImports: legacy code - style/noRestrictedImports
import { Controller, useForm } from 'react-hook-form';

import { validations } from '../constants';
import type { AuthInnerFormProps } from '../types';
import { getNextUrl, inIframe } from '../utils';

const SignIn: React.FC<React.PropsWithChildren<AuthInnerFormProps>> = ({
  nextUrl: _nextUrl,
  onCustomSuccess,
  emailDefault,
  organization,
  isDark,
  removeAutofocus,
}) => {
  const { t } = useTranslation();
  const [submitError, setSubmitError] = useState<
    // we need null because the graphql types comes with that :()
    string | undefined | null
  >();
  const nextUrl = _nextUrl || getNextUrl();

  const { handleSubmit, errors, control, formState, trigger } = useForm({
    mode: 'onChange',
    defaultValues: {
      username: emailDefault || '',
      password: '',
    },
  });

  const [loginUser] = useLoginUserMutation();

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code - correctness/useExhaustiveDependencies
  useEffect(() => {
    if (emailDefault) {
      trigger('username');
    }
  }, []);

  // const organizationSlug = organization && organization.slug;

  const onSubmit = handleSubmit(async data => {
    const res = await loginUser({
      variables: {
        ...data,
        nextUrl,
        organizationSlug: organization?.slug,
      },
    });

    const { messages, success, redirect, user } = res?.data?.loginUser || {};

    if (!success && messages && messages[0]) {
      setSubmitError(messages[0].message);
      throw messages;
    }
    if (!inIframe()) {
      setRecentlyLoggedStatus();
      allowFloatingShoppingCartReminderVisibility();
      if (onCustomSuccess && user) {
        onCustomSuccess({ redirect, user, isNewUser: false });
      } else {
        window.location.replace(redirect as string);
      }
    } else {
      setRecentlyLoggedStatus();
      allowFloatingShoppingCartReminderVisibility();
      if (onCustomSuccess && user) {
        onCustomSuccess({ redirect, user, isNewUser: false });
      } else {
        window.location.replace(redirect as string);
      }
    }
  });

  const { isSubmitting, isValid } = formState;
  const isDisabled = isSubmitting || !isValid;

  return (
    <form onSubmit={onSubmit} className="mb-32">
      <div>
        <Controller
          control={control}
          name="username"
          rules={{
            required: validations.required(t),
            pattern: validations.email(t),
          }}
          render={({ onBlur, onChange, value }) => (
            <TextField
              value={value}
              onChange={event => {
                onChange(event.target.value.toLowerCase());
                if (submitError) {
                  setSubmitError('');
                }
              }}
              onBlur={onBlur}
              label={t('EMAIL', { defaultValue: 'Correo electrónico' })}
              full
              autoFocus={!removeAutofocus}
              maxLength={50}
              type="username"
              id="username"
              data-testid="username-field"
              disabled={isSubmitting}
              darkMode={isDark}
              className="mb-16"
              error={!!errors.username}
              errorText={
                errors.username?.message
                  ? t(errors.username?.message)
                  : undefined
              }
            />
          )}
        />
      </div>
      <div>
        <Controller
          control={control}
          name="password"
          rules={{
            required: validations.required(t),
            minLength: validations.minLength(t),
            maxLength: validations.maxLength(t),
          }}
          render={({ onBlur, onChange, value }) => (
            <TextField
              value={value}
              onChange={event => {
                onChange(event);
                if (submitError) {
                  setSubmitError('');
                }
              }}
              onBlur={onBlur}
              full
              type="password"
              id="password"
              maxLength={20}
              label={t('PASSWORD', {
                defaultValue: 'Contraseña',
              })}
              data-testid="password-field"
              darkMode={isDark}
              disabled={isSubmitting}
              className="mb-16"
              error={!!errors.password}
              errorText={
                errors.password?.message
                  ? t(errors.password?.message)
                  : undefined
              }
            />
          )}
        />
        {submitError && (
          <div className="mb-16">
            <span
              className="block mt-4 tw-font-semibold font-legals text-red-main"
              role="alert"
            >
              {submitError}
            </span>
          </div>
        )}
      </div>
      <PrimaryButton
        className="w-full"
        label={t('SIGN_IN', {
          defaultValue: 'Inicia sesión',
        })}
        type="submit"
        data-testid="submit-button"
        data-cy="submit"
        size="l"
        darkMode={isDark}
        disabled={isDisabled}
      />
    </form>
  );
};

export default SignIn;
